import { create } from 'zustand';
import { jobApi } from '@api/jobApi';
import { userManager } from '@providers/userManager';
import _ from 'lodash';
import { reviewApi } from '@api/reviewApi';
import { contentApi } from '@api/contentApi';
import useNotificationStore from '@/stores/notificationStore';
import useJobDescriptionsStore from '@/stores/jobDescriptionsStore';
import useCandidatePipelineStore from '@/stores/candidatePipelineStore';
import { HttpHelper } from '@helpers/httpHelper';
import { UrlManager } from '@providers/urlManager';

const CONSTANTS = {
    CACHE_DURATION: 5 * 60 * 1000,
    POLLING_INTERVAL: 15000,
    MAX_INACTIVE_TIME: 5 * 60 * 1000,
    RATE_LIMIT_RETRY_DELAY: 5000,
    ACTIVITY_THROTTLE_DELAY: 1000,
};

const formatErrorMessage = error => ({
    error: `<strong>Status Code:</strong> ${
        error.response?.status
    } <br/> <strong>Message:</strong> ${error.response?.data?.error || error.message}`,
    loading: false,
});

const useJobRequestStore = create((set, get) => ({
    jobRequests: [],
    jobRequestDashboard: {},
    jobRequestDashboardLoading: false,
    loading: false,
    error: null,
    lastFetched: null,
    lastNotifiedIds: new Set(),
    actionLoadingMessage: {},
    isWfmFormReviewSuccess: false,

    // Improved fetch with caching and retry logic
    fetchJobRequests: async (force = false, loader = true) => {
        const now = Date.now();
        const state = get();

        if (!force && state.isCacheValid(now)) {
            return;
        }

        set({ loading: loader, error: null });

        try {
            const user = userManager.getUserData();
            if (!user?.id || !user.workEmail) {
                throw new Error('User data is missing');
            }

            const response = await jobApi.getJobRequests(user.id, user.workEmail);
            const validResponse = Array.isArray(response) ? response.flat() : [];

            // Sort the validResponse by created_at in descending order
            const sortedRequests = validResponse.sort(
                (a, b) => new Date(b.created_at) - new Date(a.created_at),
            );

            const mergedRequests = _.uniqBy(
                [...sortedRequests, ...state.jobRequests],
                req => `${req.ID}-${req.location}`,
            );

            set({
                jobRequests: mergedRequests,
                loading: false,
                lastFetched: now,
                error: null,
            });
        } catch (error) {
            set(formatErrorMessage(error));

            if (error.response?.status === 429) {
                setTimeout(() => get().fetchJobRequests(true), CONSTANTS.RATE_LIMIT_RETRY_DELAY);
            }
        }
    },

    // Add helper method to check cache validity
    isCacheValid: now => {
        const state = get();
        return (
            state.lastFetched &&
            now - state.lastFetched < CONSTANTS.CACHE_DURATION &&
            state.jobRequests.length > 0
        );
    },

    fetchJobRequestDashboard: async () => {
        try {
            const user = userManager.getUserData();
            if (!user?.location) {
                throw new Error('User location is missing');
            }

            set({ jobRequestDashboardLoading: true });

            const response = await jobApi.getJobRequestDashboard(user.location);
            const flattenedResponse = Array.isArray(response) ? response.flat() : [];
            // Return first object or empty object if array is empty
            const dashboardData = flattenedResponse[0] || {};

            set({ jobRequestDashboard: dashboardData, jobRequestDashboardLoading: false });
            return dashboardData;
        } catch (error) {
            console.error('Error fetching dashboard:', error);
            set({ jobRequestDashboard: {}, jobRequestDashboardLoading: false });
            return {};
        }
    },

    // Add new method for handling review actions
    handleReviewAction: async (requestId, tableName, action, comment = '--') => {
        const user = userManager.getUserData();
        if (!user?.id) {
            throw new Error('User data is missing');
        }
        const { addNotification } = useNotificationStore.getState();

        set({ loading: true });

        const reviewPayload = {
            user: { id: user.id, location: user.location },
            action,
            comment,
            req_id: requestId,
            table_name: tableName,
        };

        const messages = [
            'Submitting review...',
            'Creating JD...',
            'Fetching updated job requests...',
            'Fetching updated job descriptions...',
        ];

        try {
            set({
                actionLoadingMessage: {
                    messages,
                    currentStep: 0,
                },
            });

            await reviewApi.submitReviewRequest(reviewPayload);

            set({
                actionLoadingMessage: { messages, currentStep: 1 },
            });

            await contentApi.reviewRequestJd({ ...reviewPayload });

            set({
                actionLoadingMessage: { messages, currentStep: 2 },
            });

            await get().fetchJobRequests(true);

            set({
                actionLoadingMessage: { messages, currentStep: 3 },
            });

            const { fetchJobDescriptions } = useJobDescriptionsStore.getState();

            await fetchJobDescriptions(true);

            set({ loading: false, actionLoadingMessage: {} });

            addNotification({
                type: 'success',
                title: 'Job Request Review',
                message: `Your Job Request has been submitted successfully}`,
                timeOut: 0,
            });

            return true;
        } catch (error) {
            set({ loading: false, error: `Failed to submit review: ${error.message}` });

            addNotification({
                type: 'error',
                title: 'Job Request Review',
                message: `Failed to submit ${action} review: ${error}`,
                timeOut: 0,
            });

            return false;
        }
    },
    handleJobRequestAction: async (action, comment, req_id, table_name) => {
        const user = userManager.getUserData();
        if (!user?.id) {
            throw new Error('User data is missing');
        }

        set({ loading: true });

        const payload = {
            user: {
                id: user.id,
                location: user.location,
            },
            action,
            comment: comment,
            req_id: req_id,
            table_name: table_name,
        };

        const messages = ['Submitting action...', 'Fetching updated job requests...'];

        try {
            set({
                actionLoadingMessage: {
                    messages,
                    currentStep: 0,
                },
            });

            await jobApi.getActionRequestUrl(payload);

            set({
                actionLoadingMessage: { messages, currentStep: 1 },
            });
            await get().fetchJobRequests(true);
            const { fetchJobDescriptions } = useJobDescriptionsStore.getState();
            await fetchJobDescriptions(true);
            set({ loading: false, actionLoadingMessage: {} });

            return true;
        } catch (error) {
            set({ loading: false, error: `Failed to submit action: ${error.message}` });

            return false;
        }
    },

    // Optimized update logic
    updateJobRequests: newRequests => {
        if (!Array.isArray(newRequests)) return;

        set(state => {
            const updatedRequests = [...state.jobRequests];
            const updatedMap = new Map(updatedRequests.map(req => [req.ID, req]));

            newRequests.forEach(newReq => {
                if (!updatedMap.has(newReq.ID) || !_.isEqual(updatedMap.get(newReq.ID), newReq)) {
                    updatedMap.set(newReq.ID, newReq);
                }
            });

            return {
                jobRequests: Array.from(updatedMap.values()),
            };
        });
    },

    // Add cleanup method
    clearStore: () => {
        set({
            jobRequests: [],
            loading: false,
            error: null,
            lastFetched: null,
        });
    },

    checkForUpdates: async () => {
        const state = get();
        if (state.loading) return;

        try {
            const user = userManager.getUserDataLocal();
            if (!user?.id || !user.workEmail) return;
            const { addNotification, notifications } = useNotificationStore.getState();
            const { fetchInterviewScheduledData, fetchCandidatePipeline } =
                useCandidatePipelineStore.getState();
            await get().fetchJobRequests(true, false);
            await fetchInterviewScheduledData(false);
            await fetchCandidatePipeline(false);
            const response = await HttpHelper.get(UrlManager.getNotificationUrl());

            // Extract notifications from response data
            const notificationsres = response?.data || [];
            const lastNotifiedIds = get().lastNotifiedIds;

            // Check if user has required roles
            const userRoles = user.role?.map(r => r.roleName) || [];
            // debugger;

            // const hasRequiredRole = userRoles.some(role =>
            //     ['Admin', 'Manager', 'Recruiter'].includes(role),
            // );

            // if (!hasRequiredRole) return;

            notificationsres.forEach(notification => {
                if (!userRoles.includes(notification.role)) return;

                const notificationId = notification.ID;
                const isDuplicate = notifications.some(n => n.id === notificationId);

                if (!isDuplicate && !notification.Seen) {
                    addNotification({
                        id: notificationId,
                        type: 'success',
                        title: notification.Title,
                        message: notification.Description,
                        timeOut: 0,
                    });
                }
            });

            // Update store with new notified IDs
            set({
                lastNotifiedIds: lastNotifiedIds,
            });
        } catch (error) {
            console.error('Error checking for updates:', error);
        }
    },

    startPolling: () => {
        let pollTimer;
        let lastUserActivity = Date.now();
        const activityEvents = ['mousemove', 'keypress', 'click'];

        const updateActivity = _.throttle(() => {
            lastUserActivity = Date.now();
        }, CONSTANTS.ACTIVITY_THROTTLE_DELAY);

        const addActivityListeners = () => {
            activityEvents.forEach(event => window.addEventListener(event, updateActivity));
        };

        const removeActivityListeners = () => {
            activityEvents.forEach(event => window.removeEventListener(event, updateActivity));
        };

        const poll = async () => {
            const inactiveTime = Date.now() - lastUserActivity;

            if (inactiveTime < CONSTANTS.MAX_INACTIVE_TIME) {
                await get().checkForUpdates();
            }

            pollTimer = setTimeout(poll, CONSTANTS.POLLING_INTERVAL);
        };

        addActivityListeners();
        poll();

        return () => {
            clearTimeout(pollTimer);
            removeActivityListeners();
        };
    },
}));

export default useJobRequestStore;
